import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { Image as ImageIcon, Palette as PaletteIcon } from '@mui/icons-material';

export type BannerStyle = 'thumbnails' | 'illustrations';

interface BannerStyleToggleProps {
  value: BannerStyle;
  onChange: (next: BannerStyle) => void;
}

const options: Array<{ id: BannerStyle; icon: React.ReactElement; tooltip: string; label: string }> = [
  { id: 'thumbnails', icon: <ImageIcon />, tooltip: 'Show document thumbnails', label: 'Thumbnails' },
  { id: 'illustrations', icon: <PaletteIcon />, tooltip: 'Show file-type illustrations', label: 'Illustrations' },
];

/**
 * Two-button pill toggle: thumbnails vs file-type illustrations. Visual style
 * matches ViewModeToggle so the search page toolbar reads as a uniform set
 * of grouped controls.
 */
export const BannerStyleToggle: React.FC<BannerStyleToggleProps> = ({ value, onChange }) => {
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        gap: '2px',
        padding: '4px',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
      })}
    >
      {options.map(({ id, icon, tooltip, label }) => {
        const isActive = id === value;
        return (
          <Tooltip key={id} title={tooltip} arrow>
            <Box
              component="button"
              onClick={() => onChange(id)}
              aria-label={label}
              aria-pressed={isActive}
              sx={(theme) => ({
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: 'none',
                background: isActive
                  ? (theme.palette.mode === 'light'
                    ? theme.palette.action.hover
                    : 'rgba(255,255,255,.08)')
                  : 'transparent',
                color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                transition: 'background 0.15s, color 0.15s',
                '& svg': { fontSize: 13 },
                '&:hover': {
                  background: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                },
              })}
            >
              {React.cloneElement(icon, { fontSize: 'inherit' })}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default BannerStyleToggle;
